import { Box, SxProps, Tab, Tabs, Theme } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ReportHeaderDetails from './reportHeaderDetails';
import ReportInfoDetails from './reportInfoDetails';
import SectionWrapper from './sectionWrapper';

import DecryptionResolver from '~/features/cryptography/DecryptionResolver';
import { useTenantSlug } from '~/features/tenant/helpers';
import { IS_ATTACHMENT } from '~/pages/customer/reports/report-details/constants';
import { ExpandableAttachment } from '~/pages/customer/reports/report-details/report-attachments/Attachment';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';
import { ReportDetail } from '~/services/api/reports/types';
import { UserService } from '~/services/api/users/endpoints';

interface ReportInfoSectionProps {
  id: string | undefined;
}

const ReportInfoSection = ({ id }: ReportInfoSectionProps) => {
  const slug = useTenantSlug();
  const reportQuery = ReportService.endpoints.report.useQuery(
    {
      tenantSlug: slug,
      referenceId: id ?? '',
    },
    {
      skip: !id || !slug,
    },
  );

  const intl = useIntl();
  const DETAILS_TAB_VALUE = 0;
  const ATTACHMENTS_TAB_VALUE = 1;

  const [selectedTab, setSelectedTab] = useState(DETAILS_TAB_VALUE);

  const flagsQuery = UserService.endpoints.flags.useQuery();

  const tabStyle: SxProps<Theme> = {
    textTransform: 'none',
    '&.Mui-selected': {
      color: (theme) => theme.palette.teal.main,
    },
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <QueryResolver query={reportQuery}>
      {(data) => (
        <SectionWrapper>
          <ReportHeaderDetails
            reporter={data.user_full_name}
            reportDate={data.created_at}
            country={data.country_verbose.name}
          />
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            TabIndicatorProps={{
              sx: {
                backgroundColor: (theme) => theme.palette.teal.main,
                height: '3px',
              },
            }}
            sx={{
              '& .MuiTabs-scroller': {
                borderBottom: '1px solid #E7E7E7',
              },
            }}
            variant='scrollable'
          >
            <Tab
              label={intl.formatMessage({ defaultMessage: 'Details' })}
              sx={tabStyle}
            />
            <Tab
              label={intl.formatMessage({ defaultMessage: 'Attachments' })}
              sx={tabStyle}
            />
          </Tabs>
          <Box display={selectedTab === DETAILS_TAB_VALUE ? 'initial' : 'none'}>
            <DecryptionResolver content={data.description.description}>
              {(decryptedText) => (
                <ReportInfoDetails
                  description={decryptedText}
                  redactedPositions={
                    Array.isArray(data.description_redacted_ranges)
                      ? (data.description_redacted_ranges as [number, number][])
                      : []
                  }
                />
              )}
            </DecryptionResolver>
          </Box>
          <Box
            display={selectedTab === ATTACHMENTS_TAB_VALUE ? 'initial' : 'none'}
          >
            <QueryResolver query={flagsQuery}>
              {(flags) => (
                <Attachments
                  data={data}
                  isAllowedToDownload={flags.is_allowed_to_download_attachments}
                />
              )}
            </QueryResolver>
          </Box>
        </SectionWrapper>
      )}
    </QueryResolver>
  );
};

export default ReportInfoSection;

function Attachments({
  data,
  isAllowedToDownload,
}: Readonly<{ data: ReportDetail; isAllowedToDownload: boolean }>) {
  // filter out all attachments that are Voice recording
  const allAttachments = data.report_informations
    .flatMap((reportInformation) => reportInformation.files)
    .filter((file) => file.attachment_type === IS_ATTACHMENT);

  return (
    <div>
      {allAttachments.map((file) => (
        <ExpandableAttachment
          key={`${file.id}-${file.original_file_name}`}
          file={file}
          isAllowedToDownload={isAllowedToDownload}
        />
      ))}
      {allAttachments.length === 0 && (
        <div className='flex justify-center items-center h-full'>
          <p className='text-gray-500'>
            <FormattedMessage defaultMessage='No attachments found' />
          </p>
        </div>
      )}
    </div>
  );
}
